import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { StatusBadge } from '../components/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Loader2, Check, X, MessageSquare, FileText, History, User, Calendar, Clock, Search, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The ISO baseline belongs to no framework: its entries carry a null framework
const ISO_FRAMEWORK_LABEL = 'Impact Factor';

// Category keys are stored in camelCase; these two are not plain words
const CATEGORY_LABELS = {
  pefScoreCalculationFactor: 'PEF score calculation factor',
  interimEcoScoreCalculationFactor: 'Interim eco-score calculation factor',
};
const categoryLabel = (key) =>
  CATEGORY_LABELS[key] || key.replace(/([A-Z])/g, ' $1').toLowerCase().replace(/^./, (c) => c.toUpperCase());

const ACTION_LABELS = { create: 'Submitted', update: 'Submitted (update)', approve: 'Approved', reject: 'Rejected' };
const ACTION_STYLES = {
  create: 'bg-blue-100 text-blue-800',
  update: 'bg-blue-100 text-blue-800',
  approve: 'bg-green-100 text-green-800',
  reject: 'bg-red-100 text-red-800',
};

const DetailRow = ({ label, value }) => (
  <div className="py-2 border-b border-gray-100 last:border-0">
    <dt className="text-xs text-muted-foreground mb-0.5">{label}</dt>
    <dd className="text-sm font-medium break-words">{value ?? '—'}</dd>
  </div>
);

const InfoCard = ({ icon: Icon, tone, label, value, sub }) => (
  <Card className="border-gray-200 h-full">
    <CardContent className="p-4 flex items-center gap-4 h-full">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone.box}`}>
        <Icon className={`w-4 h-4 ${tone.icon}`} />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-medium text-sm">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

const DetailCard = ({ title, children, testId, twoColumns = false, className = '' }) => (
  <Card className={`border-gray-200 flex flex-col ${className}`} data-testid={testId}>
    <CardHeader className="pb-2 pt-5 px-6"><CardTitle className="font-heading text-base">{title}</CardTitle></CardHeader>
    <CardContent className={`px-6 pb-5 flex-1 ${twoColumns ? 'grid grid-cols-2 gap-x-6 content-start' : ''}`}>{children}</CardContent>
  </Card>
);

// Every digit of the stored value, no rounding: plain for everyday values (0.4, 446.044), scientific notation
// (2.05e-9) only below SCIENTIFIC_BELOW or from SCIENTIFIC_FROM.
const SCIENTIFIC_BELOW = 1e-4;
const SCIENTIFIC_FROM = 1e7;
const formatFactor = (value) => {
  if (typeof value !== 'number') return value ?? '—';
  if (value === 0) return '0';
  const magnitude = Math.abs(value);
  return magnitude < SCIENTIFIC_BELOW || magnitude >= SCIENTIFIC_FROM ? value.toExponential() : String(value);
};

// Framework columns come in this order; any other framework follows
const FRAMEWORK_ORDER = [ISO_FRAMEWORK_LABEL, 'PEF single score', 'French Eco-Score (Environmental Cost)'];
const frameworkLabel = (framework) => framework || ISO_FRAMEWORK_LABEL;

const formatDate = (dateString) =>
  dateString
    ? new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

export default function LcaImpactFactorDetailPage() {
  const { id, impactId } = useParams();
  const isLiveRow = Boolean(impactId);   // an existing row no submission published: read-only, no history
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backUrl = searchParams.get('from') === 'pending' ? '/pending-approvals' : '/lca-impact-factors';

  const [submission, setSubmission] = useState(null);
  const [history, setHistory] = useState([]);
  const [factorSearch, setFactorSearch] = useState('');
  const [sort, setSort] = useState(null);   // null, 'asc' or 'desc': only the category column sorts
  const [loading, setLoading] = useState(true);
  const [approvalDialog, setApprovalDialog] = useState({ open: false, action: null });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { canApprove } = useAuth();

  const fetchSubmission = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/lca-impact-factors/${isLiveRow ? `live/${impactId}` : id}`);
      setSubmission(response.data);
    } catch (error) {
      toast.error('Failed to load LCA impact factor');
    } finally {
      setLoading(false);
    }
  }, [id, impactId, isLiveRow]);

  useEffect(() => {
    fetchSubmission();
  }, [fetchSubmission]);

  useEffect(() => {
    if (isLiveRow) return;
    axios.get(`${API_URL}/lca-impact-factors/${id}/history`)
      .then(res => setHistory(res.data.history))
      .catch(() => { /* history tabs just stay empty */ });
  }, [id, isLiveRow, submission?.version]);

  const data = submission?.data;

  // One row per impact category and indicator, one column per framework
  const frameworkColumns = useMemo(() => {
    const present = [...new Set((data?.impact_factors || []).map(entry => frameworkLabel(entry.framework)))];
    const rank = (label) => (FRAMEWORK_ORDER.includes(label) ? FRAMEWORK_ORDER.indexOf(label) : FRAMEWORK_ORDER.length);
    return present.sort((a, b) => rank(a) - rank(b));
  }, [data]);

  const categoryRows = useMemo(() => {
    const byCategory = new Map();
    (data?.impact_factors || []).forEach(entry => {
      const key = `${entry.impactCategory}|${entry.indicator || ''}`;
      if (!byCategory.has(key)) byCategory.set(key, { key, label: categoryLabel(entry.impactCategory), indicator: entry.indicator || '', unit: null, values: {} });
      const row = byCategory.get(key);
      row.values[frameworkLabel(entry.framework)] = entry.factor;
      if (!entry.framework) row.unit = entry.unit;
    });
    return [...byCategory.values()];
  }, [data]);

  const factors = useMemo(() => {
    const needle = factorSearch.trim().toLowerCase();
    const rows = categoryRows.filter(row =>
      !needle || `${row.label} ${row.indicator} ${row.unit || ''} ${Object.entries(row.values).map(([framework, value]) => `${framework} ${formatFactor(value)}`).join(' ')}`.toLowerCase().includes(needle));
    if (!sort) return rows;
    return [...rows].sort((a, b) => (sort === 'asc' ? 1 : -1) * a.label.localeCompare(b.label));
  }, [categoryRows, factorSearch, sort]);

  const toggleCategorySort = () => setSort(current => (current === 'asc' ? 'desc' : 'asc'));
  const CategorySortIcon = sort === null ? ArrowUpDown : sort === 'asc' ? ArrowUp : ArrowDown;

  const closeDialog = () => {
    setApprovalDialog({ open: false, action: null });
    setComment('');
  };

  const handleApproval = async () => {
    const { action } = approvalDialog;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/lca-impact-factors/${id}/approve`, { action, comment: comment || null });
      toast.success(`LCA impact factor ${action === 'approve' ? 'approved' : 'rejected'}`);
      closeDialog();
      if (backUrl === '/pending-approvals') navigate(backUrl);
      else fetchSubmission();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!submission) {
    return (
      <DashboardLayout>
        <div className="text-center py-24">
          <p className="text-muted-foreground">LCA impact factor not found</p>
          <Button className="mt-4" onClick={() => navigate('/lca-impact-factors')}>Back to LCA Impact Factors</Button>
        </div>
      </DashboardLayout>
    );
  }

  const comments = history.filter(entry => entry.comment);
  const approval = history.find(entry => entry.action === 'approve');
  const approvedBy = approval && (approval.user_name || approval.user_email);

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-testid="lca-detail-page">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(backUrl)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-heading text-2xl font-bold text-foreground">
                  LCA Impact Factor {isLiveRow ? `(existing #${impactId})` : `#${submission.id}`}
                </h1>
                <StatusBadge status={submission.status} />
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-300">
                  {data.methodology}
                </span>
                {data.source_database && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                    {data.source_database}
                  </span>
                )}
              </div>
              <p className="text-muted-foreground mt-1">
                {[data.category, data.sub_category, data.type, data.sub_type].filter(Boolean).join(' / ')}
              </p>
            </div>
          </div>

          {canApprove() && submission.status === 'pending' && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setApprovalDialog({ open: true, action: 'reject' })}
                data-testid="reject-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => setApprovalDialog({ open: true, action: 'approve' })}
                data-testid="approve-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Approve
              </Button>
            </div>
          )}
        </div>

        {data.is_update && (
          <Card className="border-blue-200 bg-blue-50" data-testid="update-banner">
            <CardContent className="p-4 text-sm text-blue-800">
              This updates impact factor #{data.emission_factor_impact_id}: approving replaces its fields with the ones
              below and adds the impact factors below, which it does not have yet. Its existing impact factors are not changed.
            </CardContent>
          </Card>
        )}

        {submission.status === 'rejected' && submission.comment && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800 mb-1">Rejection Reason</h3>
                <p className="text-red-700">{submission.comment}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <InfoCard icon={Calendar} tone={{ box: 'bg-blue-100', icon: 'text-blue-600' }} label="Created" value={formatDate(submission.created_at)} sub={submission.user_email && `by ${submission.user_email}`} />
          <InfoCard icon={Clock} tone={{ box: 'bg-amber-100', icon: 'text-amber-600' }} label="Last Updated" value={formatDate(history.length > 1 ? history[0].created_at : null)} />
          <InfoCard icon={User} tone={{ box: 'bg-green-100', icon: 'text-green-600' }} label="Approved By" value={approvedBy || '—'} />
        </div>

        <Tabs defaultValue="details" className="space-y-8">
          <TabsList>
            <TabsTrigger value="details" className="gap-2" data-testid="tab-details">
              <FileText className="w-4 h-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2" data-testid="tab-history">
              <History className="w-4 h-4" />
              Version History ({history.length})
            </TabsTrigger>
            <TabsTrigger value="comments" className="gap-2" data-testid="tab-comments">
              <MessageSquare className="w-4 h-4" />
              Comments ({comments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
              <DetailCard title="Impact Factor" testId="unit-section" className="h-full">
                <DetailRow label="IFID" value={submission.emission_factor_impact_id ? `#${submission.emission_factor_impact_id}` : 'New (assigned on approval)'} />
                <DetailRow label="Category" value={data.category} />
                <DetailRow label="Sub Category" value={data.sub_category} />
                <DetailRow label="Type" value={data.type} />
                <DetailRow label="Sub Type" value={data.sub_type} />
                <DetailRow label="Input Unit (denominator)" value={data.input_unit} />
                <DetailRow label="Unit Category" value={data.unit_category} />
                <DetailRow label="Method" value={data.methodology} />
                <DetailRow label="Source Database" value={data.source_database} />
                <DetailRow label="Source Database Version" value={data.source_database_version} />
                <DetailRow label="Source Database Release Year" value={data.source_database_release_year} />
              </DetailCard>

              <DetailCard title="Source Dataset" className="h-full">
                <DetailRow label="Source Dataset UUID" value={data.source_dataset_uuid} />
                <DetailRow label="Source Dataset Name" value={data.source_dataset_name} />
                <DetailRow label="Source Dataset Reference Year" value={data.source_dataset_reference_year} />
                <DetailRow label="Source URL" value={data.source_url} />
                <DetailRow label="Additional Information" value={data.additional_information} />
              </DetailCard>

              <div className="flex flex-col gap-6">
                <DetailCard title="Location" testId="location-section" twoColumns className="flex-1">
                  <DetailRow label="Continent" value={data.continent} />
                  <DetailRow label="Country" value={data.country} />
                  <DetailRow label="State" value={data.state} />
                  <DetailRow label="City" value={data.city} />
                </DetailCard>

                <DetailCard title="Data Quality Rating (DQR)" testId="dqr-section" twoColumns className="flex-1">
                  <DetailRow label="TeR" value={data.ter} />
                  <DetailRow label="GR" value={data.ger} />
                  <DetailRow label="TiR" value={data.tir} />
                  <DetailRow label="P" value={data.p} />
                </DetailCard>
              </div>
            </div>

            <Card className="border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between gap-4 px-6 py-5">
                <div>
                  <CardTitle className="font-heading text-base">
                    {data.is_update ? 'New Impact Factors' : 'Impact Factors'} ({factors.length}
                    {factors.length !== categoryRows.length ? ` of ${categoryRows.length}` : ''})
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Per 1 {data.input_unit}. The numerator (the indicator unit of each category) is not stored; it only matters when multiplying.
                  </p>
                </div>
                <div className="relative w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search category or value..."
                    value={factorSearch}
                    onChange={(e) => setFactorSearch(e.target.value)}
                    className="pl-9 h-8 input-eco"
                    data-testid="factor-search"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="table-dense pl-6">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={toggleCategorySort} data-testid="sort-category">
                          Impact Category
                          <CategorySortIcon className={`w-3.5 h-3.5 ${sort ? 'text-foreground' : 'text-muted-foreground'}`} />
                        </button>
                      </TableHead>
                      <TableHead className="table-dense">Indicator</TableHead>
                      <TableHead className="table-dense">Unit</TableHead>
                      {frameworkColumns.map(label => (
                        <TableHead key={label} className="table-dense text-right last:pr-6">{label}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {factors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={frameworkColumns.length + 3} className="text-center text-muted-foreground py-6">No impact factors match</TableCell>
                      </TableRow>
                    ) : factors.map((row, idx) => (
                      <TableRow key={row.key} className={`table-dense ${idx % 2 === 1 ? 'bg-gray-100' : 'bg-white'}`}>
                        <TableCell className="text-sm py-2.5 pl-6">{row.label}</TableCell>
                        <TableCell className="text-sm py-2.5">{row.indicator || '—'}</TableCell>
                        <TableCell className="text-sm py-2.5">{row.unit || '—'}</TableCell>
                        {frameworkColumns.map(label => (
                          <TableCell key={label} className="font-mono text-sm text-right py-2.5 last:pr-6">{row.values[label] === undefined ? '—' : formatFactor(row.values[label])}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card className="border-gray-200">
              <CardHeader><CardTitle className="font-heading text-lg">Version History</CardTitle></CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No history yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {history.map((entry) => (
                      <div key={entry.version} className="p-4 border rounded-lg" data-testid={`history-entry-${entry.version}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-muted-foreground">v{entry.version}</span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${ACTION_STYLES[entry.action] || 'bg-gray-100 text-gray-800'}`}>
                              {ACTION_LABELS[entry.action] || entry.action}
                            </span>
                          </div>
                          <span className="text-sm text-muted-foreground">{formatDate(entry.created_at)}</span>
                        </div>
                        {entry.comment && <p className="text-sm mb-2">"{entry.comment}"</p>}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="w-4 h-4" />
                          <span className="font-medium">{entry.user_name}</span>
                          <span>({entry.user_email})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="comments">
            <Card className="border-gray-200">
              <CardHeader><CardTitle className="font-heading text-lg">Comments</CardTitle></CardHeader>
              <CardContent>
                {comments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No comments yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {comments.map((entry) => (
                      <div
                        key={entry.version}
                        className={`p-4 border rounded-lg ${entry.action === 'reject' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${ACTION_STYLES[entry.action]}`}>
                            {entry.action === 'reject' ? 'Rejection Feedback' : 'Approval Note'}
                          </span>
                          <span className="text-sm text-muted-foreground">{formatDate(entry.created_at)}</span>
                        </div>
                        <p className="text-sm mb-2">{entry.comment}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="w-4 h-4" />
                          <span className="font-medium">{entry.user_name}</span>
                          <span>({entry.user_email})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={approvalDialog.open} onOpenChange={(open) => !open && closeDialog()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{approvalDialog.action === 'approve' ? 'Approve' : 'Reject'} LCA Impact Factor</DialogTitle>
              <DialogDescription>
                {approvalDialog.action === 'approve'
                  ? `This will add the impact factors to the emission_factor_impacts table in preprod and production.`
                  : 'Please provide a reason for rejecting these impact factors.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="comment">
                Comment {approvalDialog.action === 'reject' && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={approvalDialog.action === 'reject' ? 'Please provide reason for rejection' : 'Optional comment'}
                rows={3}
                data-testid="approval-comment"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeDialog}>Cancel</Button>
              <Button
                onClick={handleApproval}
                disabled={submitting || (approvalDialog.action === 'reject' && !comment)}
                className={approvalDialog.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                data-testid="confirm-approval-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {approvalDialog.action === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
